import { Tier, TIERS } from '@backrs_ui/common-ui';

import { determineIfEntrepreneurshipRecommended } from './determineEntrepreneurship';
import { determineIfTradeRecommended } from './determineTrade';
import { CareerQuestion, CareerUserResponseAnswer, CareerUserResponseSet } from '../models/models';

export function determineTierFromResults(careerResults: ScoreCalculationResult): Tier {
  switch (true) {
    case careerResults.trade:
      return TIERS.TRADE;
    case careerResults.mid:
      return TIERS.MID;
    case careerResults.upper:
      return TIERS.UPPER;
    case careerResults.top:
      return TIERS.TOP;
    default:
      return TIERS.SEMISKILLED;
  }
}

export type ScoreCalculationResult = Readonly<{
  sectionAScore: number;
  semiSkilled: boolean;
  trade: boolean;
  mid: boolean;
  upper: boolean;
  top: boolean;
  entrepreneurship: boolean;
}>;

const SEMISKILLED_HIGH = 4;
const TRADE_HIGH = 16;
const MID_HIGH = 16;
const UPPER_HIGH = 29;
const UPPER_LOW = 15;
const TOP_LOW = 28;

export function calculateSurveyResults(
  responseSet: CareerUserResponseSet,
  /** we pass in CAREER_QUESTIONS to keep the calculator functions pure, as CAREER_QUESTIONS will eventually come from the database */
  questions: CareerQuestion[],
): ScoreCalculationResult {
  const sectionAScore = getTotalPointsFromAnswers(responseSet.user_responses, questions);

  const semiSkilled = sectionAScore < SEMISKILLED_HIGH;

  const trade =
    determineIfTradeRecommended(responseSet, questions) &&
    !semiSkilled &&
    sectionAScore < TRADE_HIGH;

  const mid = !semiSkilled && sectionAScore < MID_HIGH;

  const upper = sectionAScore > UPPER_LOW && sectionAScore < UPPER_HIGH;

  const top = sectionAScore > TOP_LOW;

  const entrepreneurship = determineIfEntrepreneurshipRecommended(responseSet, questions);

  return {
    sectionAScore,
    semiSkilled,
    trade,
    mid,
    upper,
    top,
    entrepreneurship,
  };
}

function getTotalPointsFromAnswers(
  answers: CareerUserResponseAnswer[],
  questions: CareerQuestion[],
): number {
  return answers
    .map((answer) => calculateAnswerScore(answer, questions))
    .reduce((total, score) => total + score, 0);
}

function calculateAnswerScore(
  answer: CareerUserResponseAnswer,
  questions: CareerQuestion[],
): number {
  const question = questions.find((q) => q.id === answer.career_question_id);

  if (!question) {
    return 0;
  }

  /** for any multi select question, we only want to take the highest point value */
  const highestAnswer = answer.career_question_option_ids.reduce((maxValue, optionId) => {
    const option = question.options.find((o) => o.id === optionId);

    const pointValue = option?.point_value || 0;

    return pointValue > maxValue ? pointValue : maxValue;
  }, 0);

  return highestAnswer;
}
